/**
 * Early Access Gate Component
 * Controls access to the platform homepage for unauthenticated users
 * 
 * Gate Decision Priority:
 * 1. If authenticated → show homepage (never show gate)
 * 2. Else if tc_explore_enabled flag is true and not expired → show homepage
 * 3. Else → redirect to /early-access
 * 
 * The explore flag has a 7-day TTL and persists across sessions.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// TTL for explore flag: 7 days in milliseconds
const EXPLORE_FLAG_TTL = 7 * 24 * 60 * 60 * 1000;
const EXPLORE_FLAG_KEY = 'tc_explore_enabled';

// Routes that should always bypass the gate
const BYPASS_ROUTES = new Set([
  '/early-access',
  '/auth',
  '/login',
  '/signup',
  '/sign-in',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/privacy',
  '/terms',
  '/dmca',
  '/content-guidelines',
  '/support',
  '/contact',
]);

// Route prefixes that should bypass the gate
const BYPASS_PREFIXES = [
  '/admin',
  '/site-manager',
  '/creator-studio',
  '/creator-dashboard',
  '/dashboard',
  '/comics',
  '/novels',
  '/gallery',
  '/publishers',
  '/publisher/',
  '/u/',
  '/series/',
  '/novel/',
  '/comic/',
  '/artwork/',
  '/auth/',
];

/**
 * Check if current path should bypass the gate
 */
const shouldBypassGate = (pathname) => {
  // Exact match
  if (BYPASS_ROUTES.has(pathname)) return true;
  
  // Prefix match
  for (const prefix of BYPASS_PREFIXES) {
    if (pathname.startsWith(prefix)) return true;
  }
  
  return false;
};

/**
 * Check if user has a valid explore flag
 * Returns true if flag exists and hasn't expired
 */
const hasValidExploreFlag = () => {
  try {
    const flagData = localStorage.getItem(EXPLORE_FLAG_KEY);
    if (!flagData) return false;
    
    const parsed = JSON.parse(flagData);
    if (!parsed.enabled) return false;
    
    // Check expiration
    if (typeof parsed.expiresAt === 'number' && Date.now() > parsed.expiresAt) {
      localStorage.removeItem(EXPLORE_FLAG_KEY);
      return false;
    }
    
    return true;
  } catch (e) {
    // Invalid JSON or other error - clear it
    localStorage.removeItem(EXPLORE_FLAG_KEY);
    return false;
  }
};

/**
 * Set the explore flag with TTL (7 days)
 */
export const setExploreFlag = () => {
  const flagData = {
    enabled: true,
    expiresAt: Date.now() + EXPLORE_FLAG_TTL,
    setAt: new Date().toISOString(),
  };
  localStorage.setItem(EXPLORE_FLAG_KEY, JSON.stringify(flagData));
};

/**
 * Clear the explore flag (for returning to Early Access)
 */
export const clearExploreFlag = () => {
  localStorage.removeItem(EXPLORE_FLAG_KEY);
};

/**
 * Check if user is authenticated
 */
const isUserAuthenticated = (isAuthenticatedProp) => {
  if (isAuthenticatedProp) return true;
  const token = localStorage.getItem('token');
  return !!token;
};

/**
 * Determine if we should redirect to early access
 */
const shouldRedirectToEarlyAccess = (pathname, isAuthenticatedProp) => {
  // Only redirect from homepage
  if (pathname !== '/') return false;
  
  // Don't redirect if authenticated
  if (isUserAuthenticated(isAuthenticatedProp)) return false;
  
  // Don't redirect if has valid explore flag
  if (hasValidExploreFlag()) return false;
  
  // Should redirect
  return true;
};

const EarlyAccessGate = ({ children, isAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);

  // Memoize the redirect check
  const checkAndRedirect = useCallback(() => {
    const currentPath = location.pathname;
    
    // Skip bypass routes
    if (shouldBypassGate(currentPath)) {
      return;
    }
    
    // Check if we need to redirect
    if (shouldRedirectToEarlyAccess(currentPath, isAuthenticated)) {
      if (!hasRedirected) {
        setHasRedirected(true);
        navigate('/early-access', { replace: true });
      }
    }
  }, [location.pathname, isAuthenticated, hasRedirected, navigate]);

  // Run check on mount and when dependencies change
  useEffect(() => {
    checkAndRedirect();
  }, [checkAndRedirect]);

  // Reset redirect flag when leaving homepage
  useEffect(() => {
    if (location.pathname !== '/' && hasRedirected) {
      setHasRedirected(false);
    }
  }, [location.pathname, hasRedirected]);

  return children;
};

export default EarlyAccessGate;
